import { useEffect, useRef, useState } from "react";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import DOMPurify from "dompurify";

import { ActivityNote, fetchActivityNotes } from "@/api/activities";

import S from "./ActivityMessages.module.scss";

interface ActivityMessagesProps {
  activityId: number;
  initialNotes?: ActivityNote[];
}

export const ActivityMessages = ({ activityId, initialNotes }: ActivityMessagesProps) => {
  const [notes, setNotes] = useState<ActivityNote[]>(initialNotes ?? []);
  const [loading, setLoading] = useState(!initialNotes);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await fetchActivityNotes(activityId);
      setNotes(data.activityNotes ?? []);
    } catch (err) {
      setError("Failed to load messages");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activityId && !initialNotes) {
      void loadNotes();
    }
  }, [activityId]);

  useEffect(() => {
    if (initialNotes) {
      setNotes(initialNotes);
    }
  }, [initialNotes]);

  useEffect(() => {
    if (!loading && notes.length > 0) {
      scrollToBottom();
    }
  }, [notes, loading]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} width="70%" height="60px" />
        ))}
      </div>
    );
  }

  if (error) {
    return <Message severity="error" text={error} />;
  }

  return (
    <>
      <div className={S.messagesContainer}>
        {notes.length === 0 && (
          <Message
            severity="info"
            text="No messages yet for this activity."
            pt={{ root: { role: "status" } }}
          />
        )}

        {notes.map(note => (
          <div
            key={note.id}
            className={`${S.messageRow} ${note.fromAdmin ? S.messageRowOutgoing : S.messageRowIncoming}`}
          >
            <div className={`${S.marker} ${note.fromAdmin ? S.markerOutgoing : S.markerIncoming}`}>
              <i className={`pi ${note.fromAdmin ? "pi-user" : "pi-user-edit"}`} />
            </div>
            <div
              className={`${S.messageContent} ${note.fromAdmin ? S.messageOutgoing : S.messageIncoming}`}
            >
              <div className={S.messageHeader}>
                <div className="flex items-center gap-2">
                  <strong>{note.userName}</strong>
                  <span className={S.timestamp}>{note.humanCreatedAt}</span>
                </div>
              </div>
              {note.title && <h4 className="atlas-h6 mt-2">{note.title}</h4>}
              <div
                className="ql-editor mt-2"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(note.text, { USE_PROFILES: { html: true } }),
                }}
              />
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
};
